import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Filter, Globe, Map as MapIcon, RefreshCw } from "lucide-react";
import { useKioskScans } from "../../hooks/useKioskScans";
import { HospitalMap } from "../../components/kiosk/HospitalMap";
import { CheckpointHistory, type CheckpointSummary } from "../../components/CheckpointHistory";
import { HospitalSelector } from "../../components/dashboard/HospitalSelector";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { ScanMapPage, TIME_RANGES, startDateForRange, type TimeRangeKey } from "./ScanMapPage";
import { useAuthStore } from "../../state/auth";
import type { RecentScan } from "../../services/scans";
import { fetchCompletedWorlds, type SpatialWorld } from "../../services/worlds";
import { cn } from "../../lib/utils";
import { LiveComplianceStrip } from "../../components/compliance/ComplianceRate";
import { useTranslation, type LangCode } from "../../i18n";

type TFn = (key: string, fallback?: string) => string;

const MSK_HOSPITAL_ID = "c3e35c34-eed1-40ff-a34f-7701f6c07fa3";
const EUH_HOSPITAL_ID = "b1e35c34-eed1-40ff-a34f-7701f6c07fa3";

// Only DB-backed checkpoints (uuid location ids) can be drilled into via the
// history endpoint; unmapped/legacy scan ids are not openable.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STATUS_FILTERS: Array<{ labelKey: string; labelFallback: string; value: RecentScan["status"] | "ALL" }> = [
  { labelKey: "live.filterAll", labelFallback: "All activity", value: "ALL" },
  { labelKey: "live.filterValid", labelFallback: "Valid scans", value: "OK" },
  { labelKey: "live.filterInvalid", labelFallback: "Invalid scans", value: "INVALID" }
];

export function LiveActivityPage() {
  const { t, lang } = useTranslation();
  const { user } = useAuthStore();
  // Default to "all my hospitals" (undefined). For a SUPERVISOR the scan feed is
  // scoped server-side to their assigned hospitals, so this shows exactly their
  // hospitals aggregated; they can narrow to one via the selector below.
  const [selectedHospitalId, setSelectedHospitalId] = useStickyHospital();

  // Shared time-range filter — the single source of truth for both the embedded
  // Scan Map and the activity feed below it. Uses the Scan Map's range vocabulary
  // (1h/24h/7d/30d/custom) so selecting "Last 30 days" reflows the feed too.
  const [range, setRange] = useState<TimeRangeKey>("24h");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  // Resolve the range to explicit start/end ISO dates for the feed query.
  // Memoized on the range inputs so "now"-relative starts stay stable between
  // polls and only recompute when the user changes the filter.
  const { feedStart, feedEnd } = useMemo(() => {
    if (range === "custom") {
      return {
        feedStart: customStart ? new Date(customStart).toISOString() : undefined,
        feedEnd: customEnd ? new Date(customEnd).toISOString() : undefined,
      };
    }
    const start = startDateForRange(range);
    return { feedStart: start ? start.toISOString() : undefined, feedEnd: undefined };
  }, [range, customStart, customEnd]);

  // Human-readable label for the active range, surfaced on the hospital map.
  const rangeLabel = TIME_RANGES.find((r) => r.key === range)?.label ?? "";

  const feed = useKioskScans(5000, undefined, selectedHospitalId, undefined, feedStart, feedEnd);
  const { scans, loading } = feed;
  const [statusFilter, setStatusFilter] = useState<RecentScan["status"] | "ALL">("ALL");
  // Checkpoint whose scan-history drill-down panel is open (null = closed).
  const [historyCheckpoint, setHistoryCheckpoint] = useState<CheckpointSummary | null>(null);

  // Completed 3D worlds for this hospital, keyed by checkpoint_id. Refreshed
  // alongside the scan feed so the globe icon appears as worlds finish generating.
  const [worlds, setWorlds] = useState<Map<string, SpatialWorld>>(new Map());
  useEffect(() => {
    let active = true;
    const load = () => {
      void fetchCompletedWorlds(selectedHospitalId).then((m) => {
        if (active) setWorlds(m);
      });
    };
    load();
    const interval = window.setInterval(load, 30000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [selectedHospitalId]);

  // A scan's checkpoint can be identified by either its location code or name.
  const worldFor = (scan: RecentScan): SpatialWorld | undefined =>
    worlds.get(scan.location_id) || (scan.location_name ? worlds.get(scan.location_name) : undefined);
  const isMSK = selectedHospitalId === MSK_HOSPITAL_ID;
  const isEUH = selectedHospitalId === EUH_HOSPITAL_ID;
  const filtered = useMemo(
    () => (statusFilter === "ALL" ? scans : scans.filter((scan) => scan.status === statusFilter)),
    [scans, statusFilter]
  );

  const isAdmin = user?.role === "ADMIN" || user?.role === "Administrator";
  const isDirector = user?.role === "DIRECTOR";
  const isSupervisor = user?.role === "SUPERVISOR";
  // SUPERVISOR gets the (role-scoped) hospital selector and hospital column too.
  const canSelectHospital = isAdmin || isDirector || isSupervisor;

  return (
    <div className="space-y-6 p-6 max-w-[1600px] mx-auto">
      {(isAdmin || isDirector || isSupervisor) && (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          {/* The embedded map renders its own "GuardAI Scan Map" title, so the
              separate "Scan Map" section header above it was redundant. */}
          <ScanMapPage
            range={range}
            onRangeChange={setRange}
            customStart={customStart}
            onCustomStartChange={setCustomStart}
            customEnd={customEnd}
            onCustomEndChange={setCustomEnd}
          />
        </section>
      )}

      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('live.title', 'Live Patrol Activity')}</h1>
          <p className="text-sm text-gray-600">{t('live.subtitle', 'A real-time feed of every badge scan as it happens')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-4 bg-gray-50/50 p-4 rounded-2xl border border-gray-200/50 backdrop-blur-sm">
          {canSelectHospital && (
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{t("domain.hospital", "Hospital")}</span>
              <HospitalSelector
                selectedHospitalId={selectedHospitalId}
                onHospitalChange={setSelectedHospitalId}
              />
            </div>
          )}

          {/* Admins/directors drive the timeframe from the Scan Map's range
              buttons above (which now also control this feed), so the duplicate
              dropdown is only shown to users without the map (e.g. supervisors). */}
          {!(isAdmin || isDirector) && (
            <div className="flex flex-col gap-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{t("live.timeframe", "Timeframe")}</span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value as TimeRangeKey)}
                className="border border-gray-200 rounded-xl px-4 py-2 text-sm bg-white shadow-sm hover:border-indigo-300 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-semibold text-gray-700 min-w-[160px]"
              >
                {TIME_RANGES.filter((r) => r.key !== "custom").map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-4 sm:pt-0">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1 opacity-0">{t("live.action", "Action")}</span>
            <button
              type="button"
              onClick={feed.refresh}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-gray-200 px-6 py-2 text-sm font-bold text-gray-700 shadow-sm hover:bg-gray-50 hover:border-gray-300 active:scale-95 transition-all h-[38px]"
            >
              <RefreshCw className={cn("h-4 w-4 text-indigo-500", loading && "animate-spin")} />
              {t("live.syncData", "Sync Data")}
            </button>
          </div>
        </div>
      </header>

      {/* Current 2-hour compliance rate for the selected hospital (or all). */}
      {canSelectHospital && <LiveComplianceStrip hospitalId={selectedHospitalId} />}

      {(isMSK || isEUH) && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => window.open(isEUH ? '/euh-3d.html' : '/msk-3d.html', '_blank')}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-indigo-700 active:scale-95 transition-all"
          >
            <Box className="h-4 w-4" />
            {t("live.view3d", "3D View")}
          </button>
        </div>
      )}

      <HospitalMap scans={filtered.length ? filtered : scans} hospitalId={selectedHospitalId} rangeLabel={rangeLabel} />


      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h2 className="text-lg font-semibold text-gray-900">{t("live.activityFeed", "Activity Feed")}</h2>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Filter className="h-4 w-4" />
            <select
              className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey, option.labelFallback)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">{t("live.colDateTime", "Date & time")}</th>
                <th className="px-3 py-2 text-left font-semibold">{t("common.status", "Status")}</th>
                <th className="px-3 py-2 text-left font-semibold">{t("live.colDistance", "Distance")}</th>
                <th className="px-3 py-2 text-left font-semibold">{t("live.colCoordinate", "Coordinate")}</th>
                {canSelectHospital && <th className="px-3 py-2 text-left font-semibold">{t("domain.hospital", "Hospital")}</th>}
                <th className="px-3 py-2 text-left font-semibold">{t("common.location", "Location")}</th>
                <th className="px-3 py-2 text-left font-semibold">{t("live.colOfficer", "Officer")}</th>
                <th className="px-3 py-2 text-left font-semibold">{t("domain.device", "Device")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={canSelectHospital ? 8 : 7} className="px-3 py-8 text-center text-gray-600">
                    {t("live.loadingActivity", "Loading patrol activity…")}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={canSelectHospital ? 8 : 7} className="px-3 py-8 text-center text-gray-600">
                    {t("live.noScansFilter", "No scans matching this filter.")}
                  </td>
                </tr>
              ) : (
                filtered.map((scan) => (
                  <ActivityRow
                    key={scan.id}
                    scan={scan}
                    showHospital={canSelectHospital}
                    world={worldFor(scan)}
                    onSelectCheckpoint={setHistoryCheckpoint}
                    t={t}
                    lang={lang}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {historyCheckpoint && (
        <CheckpointHistory
          checkpoint={historyCheckpoint}
          onClose={() => setHistoryCheckpoint(null)}
        />
      )}
    </div>
  );
}

function ActivityRow({ scan, showHospital, world, onSelectCheckpoint, t, lang }: { scan: RecentScan; showHospital: boolean; world?: SpatialWorld; onSelectCheckpoint: (cp: CheckpointSummary) => void; t: TFn; lang: LangCode }) {
  const canDrill = UUID_RE.test(scan.location_id);
  return (
    <tr className={scan.status === "INVALID" ? "bg-red-50" : scan.status === "ERROR" ? "bg-amber-50" : ""}>
      <td className="px-3 py-2 text-gray-800">
        <p className="font-medium">{formatDate(scan.timestamp, lang)}</p>
        <p className="text-xs text-gray-500">{formatTime(scan.timestamp, lang)}</p>
      </td>
      <td className="px-3 py-2">
        <StatusBadge status={scan.status} />
        {scan.proximity_distance_m && scan.proximity_distance_m > 100 && (
          <p className="text-[10px] text-red-600 mt-0.5 font-medium">
            {t("live.metersAway", "{n}m away").replace("{n}", String(Math.round(scan.proximity_distance_m)))}
          </p>
        )}
        {scan.message && (
          <p className="text-[10px] text-gray-500 mt-0.5 max-w-[160px]">{scan.message}</p>
        )}
      </td>
      <td className="px-3 py-2">
        <DistanceBadge distance={scan.proximity_distance_m} />
      </td>
      <td className="px-3 py-2 text-gray-900 font-mono text-xs">
        {scan.gps_lat && scan.gps_lng ? (
          <div className="flex flex-col gap-0.5">
            <a
              href={`https://www.google.com/maps?q=${scan.gps_lat},${scan.gps_lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {scan.gps_lat.toFixed(5)}, {scan.gps_lng.toFixed(5)}
            </a>
            {scan.gps_source === 'DEVICE' && (
              <span className="text-[10px] text-amber-600 font-sans italic">
                {t("live.deviceLocation", "(Device Location)")}
              </span>
            )}
            {scan.gps_source === 'EVENT' && (
              <span className="text-[10px] text-emerald-600 font-sans italic">
                {t("live.freshFix", "(Fresh Fix)")}
              </span>
            )}
            {scan.gps_timestamp && (
              <span className="text-[9px] text-gray-400 font-sans">
                {t("live.asOf", "As of {time}").replace("{time}", new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }).format(new Date(scan.gps_timestamp)))}
              </span>
            )}
          </div>
        ) : (
          <span className="text-gray-400">{t("live.noGpsData", "No GPS data")}</span>
        )}
      </td>
      {showHospital && (
        <td className="px-3 py-2">
          <p className="font-medium text-gray-900">{scan.hospital_name || t("live.unknownHospital", "Unknown Hospital")}</p>
        </td>
      )}
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          {canDrill ? (
            <button
              type="button"
              onClick={() =>
                onSelectCheckpoint({
                  id: scan.location_id,
                  name: scan.location_name,
                  building: scan.building,
                  floor: scan.floor != null ? String(scan.floor) : null,
                  hospital_name: scan.hospital_name,
                })
              }
              className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
              title={t("checkpoints.viewHistory", "View scan history")}
            >
              {scan.location_name || t("live.unknownLocation", "Unknown Location")}
            </button>
          ) : (
            <p className="font-medium text-gray-900">{scan.location_name || t("live.unknownLocation", "Unknown Location")}</p>
          )}
          {world && (
            <button
              type="button"
              onClick={() => window.open(world.marble_url, "_blank", "noopener,noreferrer")}
              title={t("live.view3dWorld", "View 3D world of this checkpoint")}
              aria-label={t("live.view3dWorld", "View 3D world of this checkpoint")}
              className="inline-flex items-center justify-center rounded-full p-0.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 transition-colors"
            >
              <Globe className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          {scan.building} • {t("live.floor", "Floor {n}").replace("{n}", String(scan.floor))}
        </p>
      </td>
      <td className="px-3 py-2">
        {/* Officer who initiated the scan (signed on with their N number).
            Unattributed = no officer was signed on when the scan happened. */}
        {scan.officer_name ? (
          scan.officer_id ? (
            <Link to={`/supervisor/officers/${scan.officer_id}`} className="group block">
              <p className="font-medium text-[#3B7BBE] group-hover:underline">{scan.officer_name}</p>
              {scan.officer_n_number && (
                <p className="text-xs text-gray-500 font-mono">{scan.officer_n_number}</p>
              )}
            </Link>
          ) : (
            <>
              <p className="font-medium text-gray-900">{scan.officer_name}</p>
              {scan.officer_n_number && (
                <p className="text-xs text-gray-500 font-mono">{scan.officer_n_number}</p>
              )}
            </>
          )
        ) : (
          <span className="text-xs font-medium text-gray-400">
            {t("live.unattributed", "Unattributed")}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        {/* One line only. The subtitle used to print the raw signed-in guard
            account, but several handsets are signed in under accounts named for
            a different site (e.g. "Northlake Device 1" on an EMSK device), which
            is exactly what display_device_name rewrites for the line above —
            so the subtitle either leaked the misleading raw name or duplicated
            the line above. The raw account remains in the API response for
            anyone auditing attribution. */}
        <p className="font-medium text-gray-900">{formatDeviceName(scan.display_device_name, scan.guard_name, scan.guard_id)}</p>
      </td>
    </tr>
  );
}

function DistanceBadge({ distance }: { distance?: number | null }) {
  if (distance === null || distance === undefined) {
    return <span className="text-gray-400 text-xs font-medium">—</span>;
  }
  const rounded = Math.round(distance);
  const colorClass = rounded < 20 ? "text-emerald-600" : "text-rose-600";
  return <span className={`${colorClass} text-xs font-semibold`}>{rounded}m</span>;
}

function StatusBadge({ status, message }: { status: RecentScan["status"]; message?: string }) {
  const classes = {
    OK: "bg-emerald-100 text-emerald-700",
    INVALID: "bg-amber-100 text-amber-800",
    ERROR: "bg-rose-100 text-rose-800"
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-3 py-1 text-xs font-semibold ${classes[status]}`}>
      {status}
      {message && <span className="text-[11px] text-gray-700">{message}</span>}
    </span>
  );
}

function formatGuardName(raw?: string | null) {
  if (!raw) return "—";
  const cleaned = raw.replace(/[_-]/g, " ").replace(/\s+/g, " ").trim();
  return cleaned || "—";
}

// Some rows (e.g. officer-attributed scans with no device account name) arrive
// with the literal string "null" rather than a JS null, so a plain `||` chain
// prints "null". Treat those as empty and fall through to a clean em dash.
function isBlank(v?: string | null): boolean {
  if (v == null) return true;
  const s = String(v).trim().toLowerCase();
  return s === "" || s === "null" || s === "undefined";
}
function formatDeviceName(displayName?: string | null, guardName?: string | null, guardId?: string | null) {
  if (!isBlank(displayName)) return displayName as string;
  if (!isBlank(guardName)) return guardName as string;
  if (!isBlank(guardId)) return formatGuardName(guardId);
  return "—";
}

function formatDate(ts: string, lang: LangCode) {
  return new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", year: "numeric" }).format(new Date(ts));
}

function formatTime(ts: string, lang: LangCode) {
  return new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(new Date(ts));
}
